import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { usersApi } from '@/lib/api';
import { MagnifyingGlass, User, Star, Wrench, Phone } from '@phosphor-icons/react';

export const FocTechnicians = () => {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchTechnicians = async () => {
      try {
        const { data } = await usersApi.list({ role: 'technician' });
        setTechnicians(data || []);
      } catch (error) {
        toast.error('Failed to load technicians');
      } finally {
        setLoading(false);
      }
    };
    fetchTechnicians();
  }, []);

  const filteredTechs = technicians.filter(t =>
    t.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.phone?.includes(searchQuery)
  );

  return (
    <DashboardLayout role="foc">
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-black tracking-tight mb-2">Technicians</h1>
          <p className="text-muted-foreground">View all field technicians in your organization</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <Card className="p-4 border">
            <p className="label-micro mb-1">Total Technicians</p>
            <p className="text-3xl font-black">{technicians.length}</p>
          </Card>
          <Card className="p-4 border">
            <p className="label-micro mb-1">Active</p>
            <p className="text-3xl font-black text-emerald-600">
              {technicians.filter(t => t.is_active).length}
            </p>
          </Card>
          <Card className="p-4 border">
            <p className="label-micro mb-1">Inactive</p>
            <p className="text-3xl font-black text-slate-400">
              {technicians.filter(t => !t.is_active).length}
            </p>
          </Card>
        </div>

        {/* Search */}
        <div className="mb-6 relative max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Technician List */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-32 bg-slate-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : filteredTechs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTechs.map((tech) => (
              <Card key={tech.id} className="p-5 border">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                    tech.is_active ? 'bg-emerald-500' : 'bg-slate-300'
                  }`}>
                    {tech.full_name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm">{tech.full_name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Phone size={11} /> {tech.phone}
                    </p>
                    {tech.email && (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{tech.email}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                        tech.is_active
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-slate-100 text-slate-500'
                      }`}>
                        {tech.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center border">
            <Wrench size={48} className="mx-auto mb-4 text-muted-foreground/30" />
            <p className="text-muted-foreground">No technicians found</p>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};
